import { Clock, MapPin, User } from "lucide-react";
import { Button } from "@baluu/ui/components/button";
import { markdownToPlainText } from "@baluu/ui/lib/markdown-to-plain-text";
import type { BundleCourse, ManageTutor } from "./types";
import { tutorName } from "./bundleManage";

// The bundle page's Services tab (v1's AvailableServices): the listings the
// bundle can be used on, each with "Book now".

export function ServicesTab({
  courses,
  tutors,
  canBook,
  onBook,
}: {
  courses: BundleCourse[];
  tutors?: ManageTutor[];
  canBook: boolean;
  onBook: (course: BundleCourse) => void;
}) {
  if (courses.length === 0) {
    return <p className="py-8 text-center text-muted-foreground">No services available at the moment</p>;
  }
  return (
    <ul className="space-y-4">
      {courses.map((course) => {
        // The flattened text: a short description that's only an image or
        // `&nbsp;` is truthy but reads as nothing.
        const preview = markdownToPlainText(course.shortDescription) || markdownToPlainText(course.description);
        const tutor = tutorName(tutors, course.tutor);
        return (
          <li key={course._id} className="overflow-hidden rounded-lg border transition-shadow hover:shadow-md">
            <div className="flex flex-col sm:flex-row">
              {course.thumbnail && (
                <div className="h-32 w-full shrink-0 sm:w-32">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={course.thumbnail} alt="" className="h-full w-full object-cover" />
                </div>
              )}
              <div className="flex-1 p-4">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex-1">
                    <h3 className="mb-1 font-semibold">{course.title}</h3>
                    {course.category && (
                      <span className="mb-2 inline-flex rounded-md bg-muted px-2 py-0.5 text-xs font-medium">{course.category}</span>
                    )}
                    {preview && <p className="mb-3 line-clamp-2 text-sm text-muted-foreground">{preview}</p>}
                    <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                      {!!course.duration && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" aria-hidden />
                          {course.duration} min
                        </span>
                      )}
                      {course.isOnline ? (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-4 w-4" aria-hidden />
                          Online
                        </span>
                      ) : (
                        course.summary?.nextAvailableLocation && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" aria-hidden />
                            {course.summary.nextAvailableLocation}
                          </span>
                        )
                      )}
                      {tutor && (
                        <span className="flex items-center gap-1">
                          <User className="h-4 w-4" aria-hidden />
                          {tutor}
                        </span>
                      )}
                    </div>
                  </div>
                  <Button className="w-full sm:w-auto" disabled={!canBook} onClick={() => onBook(course)}>
                    Book now
                  </Button>
                </div>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
